import { atom, selector } from 'recoil';
import { OfferContent } from '~/common/models';
import { EnquiryForm } from '~/common/components/offer-enquiry-form';
import { ActiveSpecialOffer } from '../special-offer/store';
import { resolveOfferCta } from './cta-template';

/**
 * State for the redesigned offer page.
 *
 * The offer itself stays in `ActiveSpecialOffer` from the existing page's store - one atom, one offer, both
 * designs read it - so nothing here duplicates a Recoil key. Only what the redesign adds lives here.
 */

/**
 * The enquiry form resolved for this offer, or null when the offer has no form and no default exists.
 * The redesign renders it on the page instead of sending the customer to a separate enquiry page.
 */
export const ActiveOfferEnquiryForm = atom<EnquiryForm | null>({
  key: 'ActiveOfferEnquiryForm',
  default: null,
});

/**
 * The form the page will actually render, which is not quite the same as the one that was resolved.
 *
 * A form with no fields is discarded here. That is what a failed read looks like by the time it has been
 * deserialised into the shape of a form - the site's Refit clients are configured never to throw on an error
 * status, so an error body becomes an object with every property at its default. Rendering it would put a
 * lone Submit button under a heading. One rule, in one place, so the enquiry button and the enquiry section
 * cannot disagree about whether there is a form.
 */
export const PageEnquiryForm = selector<EnquiryForm | null>({
  key: 'PageEnquiryForm',
  get: ({ get }) => {
    const form = get(ActiveOfferEnquiryForm);
    return form && form.fields?.length > 0 ? form : null;
  },
});

/**
 * Whether the band beside the enquiry form shows the test drive panel.
 *
 * The offer stores the negative (`hideTestDrivePanel`, so an absent attribute keeps today's behaviour) and
 * the section renders the positive, so the inversion is done once here rather than at each `if`. With no
 * offer loaded there is nothing to show a panel for, hence the false.
 */
export const ShowTestDrivePanel = selector<boolean>({
  key: 'ShowTestDrivePanel',
  get: ({ get }) => {
    const offer = get(ActiveSpecialOffer);
    return !!offer && !offer.hideTestDrivePanel;
  },
});

/**
 * The calls to action, as the redesign presents them.
 *
 * One button - Enquire - and a row of icon links beneath it. Everything the offer carries goes in that row,
 * whatever sections it was authored into.
 *
 * Sections are deliberately ignored here. They describe the previous design's layout (floating, blue box,
 * and the sub- variants of each), and the live data was authored against it years ago: most offers put
 * "Build your own" in a main section, which this page was rendering as an outlined button beside the icon
 * links. That is not what the design shows, and it made the page's shape depend on where an editor ticked a
 * box in a layout that no longer exists.
 */
/** Where the test drive button goes when no offer overrides it. */
const DEFAULT_TEST_DRIVE_HREF = '/book-test-drive';

/**
 * The CTA member of the content union, which declares `contentType` as `'CTA' | 'CONTENT-CTA'` - so an
 * Extract on `'CTA'` alone narrows to never. Only `'CTA'` is accepted at runtime, as everywhere else on this
 * page: `CONTENT-CTA` is the tile's button, mined by the offers API for the listing, not a link on the page.
 */
type StoredCta = Extract<OfferContent, { contentType: 'CTA' | 'CONTENT-CTA' }>;

function isCta(content: OfferContent): content is StoredCta {
  return content.contentType === 'CTA';
}

/**
 * The booking preset belongs to the panel beside the enquiry form and nowhere else.
 *
 * It is stored as an ordinary CTA so that one control sets the destination for both the panel and this page -
 * see TestDriveHref - but the design puts "Book a test drive" on the blue panel alone. Rendering it here as
 * well gave the same offer two test drive links a few hundred pixels apart, which reads as a mistake and
 * pushes the links that are meant to be here out of line.
 */
function isPanelOnly(content: OfferContent): boolean {
  return isCta(content) && content.content.kind === 'book-test-drive';
}

/** The buttons: CTAs meant for a main section and not repeated as a link. */
/** Every call to action the offer carries, as the row of icon links under the Enquire button. */
export const OfferCtaLinks = selector<OfferContent[]>({
  key: 'OfferCtaLinks',
  get: ({ get }) => {
    const seen = new Set<string>();

    return ((get(ActiveSpecialOffer)?.contents ?? []) as OfferContent[])
      .filter((content) => isCta(content) && !isPanelOnly(content))
      .sort((left, right) => left.displayOrder - right.displayOrder)
      .filter((content) => {
        // The live data puts one CTA in several sections at once - a brochure link is often authored as
        // ['floating-ctas', 'sub-floating-ctas'] - and now that sections are ignored, the same CTA could
        // otherwise arrive twice. The kind leads the key so that two presets are never taken for one
        // another: their wording and destination are the site's to decide, so nothing stops a future writer
        // from storing them without a snapshot, and on the stored text and link alone every such CTA would
        // hash the same and all but the first would disappear.
        if (content.contentType !== 'CTA') return false;
        const { kind, text, link } = content.content;
        const key = `${kind ?? 'custom'}|${text}|${link}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  },
});

/**
 * Where the panel's "Book a test drive" button goes.
 *
 * The destination is the site's, not the editor's - the same reasoning as every other preset - but a
 * campaign can legitimately need its own booking page, so an offer carrying the `book-test-drive` preset
 * with a destination override moves the panel's button as well as the link in the icon row. One control,
 * both places: an editor who overrode the CTA and then found the panel still pointing at the generic page
 * would have no way to tell which of the two the customer would use.
 *
 * The CTA does not have to be in any particular section. The panel is not a section, so requiring one would
 * mean the override only worked when the editor also happened to tick the row the CTA appears in.
 *
 * Falls back to the generic booking page, which is what every offer pointed at before this existed.
 */
export const TestDriveHref = selector<string>({
  key: 'TestDriveHref',
  get: ({ get }) => {
    const ctas = (get(ActiveSpecialOffer)?.contents ?? []) as OfferContent[];

    const booking = ctas
      .filter(isCta)
      .sort((left, right) => left.displayOrder - right.displayOrder)
      .find((content) => content.content.kind === 'book-test-drive');

    return booking
      ? resolveOfferCta(booking.content).href
      : DEFAULT_TEST_DRIVE_HREF;
  },
});

/**
 * Where a submitted enquiry takes the customer.
 *
 * Built by the server from the named route rather than assembled here, so the shape of the URL is stated
 * once, in the place that owns the routing. Null when the page was rendered without it, which the section
 * falls back to a path built from the offer's slug - a customer who has just had their enquiry published
 * must never be left on the form, and that is worth one duplicated route shape.
 */
export const ThankYouUrl = atom<string | null>({
  key: 'ThankYouUrl',
  default: null,
});

/**
 * The reCAPTCHA site key the enquiry form renders its check with, or null when the page was served without
 * one - a local run whose configuration has no key, for instance.
 *
 * An atom set from the page's props rather than a prop threaded through three components, since only the form
 * at the foot of the page needs it. The key is public by design; the secret half stays on the server, which
 * is what verifies the token.
 */
export const RecaptchaSiteKey = atom<string | null>({
  key: 'RecaptchaSiteKey',
  default: null,
});

/** The id the enquiry section carries, and the primary button scrolls to. */
export const ENQUIRY_ANCHOR_ID = 'offer-enquiry';
